// Server-backed data source for the pathway workspace (thin-client).
//
// The server does the heavy lifting: it derives periods + runs the pathway DSL
// over the whole cohort (cohort-stats) and serves raw rows for a specific set of
// patients (patient-timelines). The client only ever builds geometry for the
// patients it is actually rendering (the visible grid window) and reconstructs
// the lean match summaries into the shape the metric functions expect.
import type {
  PathwayFacetsResponse,
  PathwayMatchSummary,
  PathwayPatientRows,
} from "@thalamos/aspire-common";
import {
  calculateDetentionPeriods,
  getDeceasedDateTime,
  type DetentionPeriod,
  type MhaStatusRow,
  type PathwayMatch,
} from "@thalamos/pathway-logic";

import type { MhaStatusData } from "./mhaStatusData.js";
import { generateVisualisationFromPeriods } from "./vizGeometry.js";

const PATIENT_Y_OFFSET = 0.5;

function getDaysArray(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  for (
    const d = new Date(start);
    d <= new Date(end);
    d.setDate(d.getDate() + 1)
  ) {
    days.push(new Date(d));
  }
  return days;
}

/** Per-patient span flags, matching the visualiser: distinct hospital_name /
 *  trust_id over events that are not Not-Detained / Expired. */
function spanFlags(entries: MhaStatusRow[]): {
  includesMultipleHospitals: boolean;
  includesMultipleTrusts: boolean;
} {
  const active = (e: MhaStatusRow) =>
    e.mha_status !== "Not Detained" && e.mha_status !== "Expired";
  const hospitals = new Set(
    entries
      .filter((e) => e.hospital_name && active(e))
      .map((e) => e.hospital_name),
  );
  const trusts = new Set(
    entries.filter((e) => e.trust_id && active(e)).map((e) => e.trust_id),
  );
  return {
    includesMultipleHospitals: hospitals.size > 1,
    includesMultipleTrusts: trusts.size > 1,
  };
}

/**
 * Build the render dataset for just the loaded window of patients, in the
 * server's order. Periods + geometry are computed ONLY for these (~120 on
 * screen), never the whole cohort — that's the whole point of paging. Filtering
 * + DSL matching already happened server-side, so no client filtering here.
 */
export function buildWindowedData(
  facets: PathwayFacetsResponse,
  orderedIds: string[],
  timelinesById: Map<string, PathwayPatientRows>,
  totalPatients: number,
): MhaStatusData {
  const minDate = facets.dateRangeStart
    ? new Date(facets.dateRangeStart)
    : new Date();
  const maxDate = facets.dateRangeEnd
    ? new Date(facets.dateRangeEnd)
    : new Date();
  const daysArray = getDaysArray(minDate, maxDate);

  const patients: MhaStatusData["patients"] = [];
  const allFilteredPatients: MhaStatusData["allFilteredPatients"] = [];
  for (const id of orderedIds) {
    const loaded = timelinesById.get(id);
    if (loaded === undefined) continue; // not fetched yet
    // PathwayMhaStatusRow is structurally identical to pathway-logic's
    // MhaStatusRow; assign directly so TS enforces they stay compatible.
    const entries = loaded.entries;
    const detentionPeriods = calculateDetentionPeriods(entries);
    const { line, circles } = generateVisualisationFromPeriods(
      detentionPeriods,
      entries,
      daysArray,
      PATIENT_Y_OFFSET,
    );
    patients.push({
      id,
      entries,
      line,
      circles,
      assessments: loaded.assessments,
      deceasedDateTime: getDeceasedDateTime(entries),
      ...spanFlags(entries),
    });
    allFilteredPatients.push({ id, entries, detentionPeriods });
  }

  return {
    hospitals: facets.hospitals,
    trusts: facets.trusts,
    dateRange: [minDate, maxDate],
    totalPatientsInFile: totalPatients,
    patients,
    allFilteredPatients,
  };
}

/**
 * Revive a lean server match into a PathwayMatch the metric functions accept:
 * measurement dates back to Date, plus a minimal step/period carrying the
 * precomputed trusts so allTrustsOf(match) works (the full backing periods are
 * intentionally not sent over the wire).
 */
export function reviveMatch(summary: PathwayMatchSummary): PathwayMatch {
  return {
    matched: true,
    stepMatches: [
      {
        // Only `trusts` is read (by allTrustsOf); the rest of the period isn't
        // sent over the wire, so a minimal stand-in is sufficient here. Revive
        // trusts as a real Set to match DetentionPeriod's runtime shape.
        periods: [
          { trusts: new Set(summary.trusts) } as unknown as DetentionPeriod,
        ],
        startDate: new Date(0),
        endDate: new Date(0),
      },
    ],
    measurements: summary.measurements.map((m) => ({
      ...m,
      fromDate: new Date(m.fromDate),
      toDate: new Date(m.toDate),
    })),
  };
}
